/**
 * AR Aging report.
 *
 * Bucket dealer outstanding into 0-30 / 31-60 / 61-90 / 90+ day windows
 * counted from the invoice due date. Renders per-dealer rows with a
 * grand-total footer.
 *
 * Data is derived from real entity data via usePersistentMockState:
 *   - invoices → filter non-void/non-draft with outstanding balance, bucket by age
 *   - dealers  → fold in opening AR balances as phantom invoices
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { Badge, DataTable, Input, KpiCard, Label, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../../components/PageHeader";
import { usePersistentMockState } from "../../../../lib/usePersistentMockState";
import {
  mockInvoices,
  type MockInvoice,
} from "../../../../lib/invoicingMock";
import {
  mockDealers,
  type MockDealer,
} from "../../../../lib/salesMock";
import {
  type ArAgingBucket,
  type ArAgingRow,
} from "../../../../lib/glMock";
import { ExportButton } from "../../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../../lib/export";

export const Route = createFileRoute("/_app/finance/reports/ar-aging")({
  component: ArAgingPage,
});

/* ── Pure derive function ─────────────────────────────────────── */

const DAY = 24 * 60 * 60 * 1000;

function bucketForAgeDays(ageDays: number): ArAgingBucket {
  return ageDays <= 30
    ? "0-30"
    : ageDays <= 60
      ? "31-60"
      : ageDays <= 90
        ? "61-90"
        : "90+";
}

function deriveArAging(
  asOfDate: number,
  invoices: MockInvoice[],
  dealers: MockDealer[],
): {
  asOfDate: number;
  rows: ArAgingRow[];
  bucketTotals: Record<ArAgingBucket, bigint>;
  grandTotalInBaseCents: bigint;
} {
  const map = new Map<string, ArAgingRow>();

  function ensureRow(dealerId: string, dealerName: string): ArAgingRow {
    let row = map.get(dealerId);
    if (!row) {
      row = {
        dealerId,
        dealerName,
        totalInBaseCents: 0n,
        buckets: { "0-30": 0n, "31-60": 0n, "61-90": 0n, "90+": 0n },
        items: [],
        hasOpeningBalance: false,
      };
      map.set(dealerId, row);
    }
    return row;
  }

  // Build a dealer name lookup
  const dealerNameMap = new Map<string, string>();
  for (const d of dealers) {
    dealerNameMap.set(d._id, d.displayName);
  }

  // Bucket real invoices with outstanding balances
  for (const inv of invoices) {
    if (inv.status === "void" || inv.status === "draft") continue;
    const balance = inv.totalAmountInBaseCents - inv.paidAmountInBaseCents;
    if (balance <= 0n) continue;
    // Only include invoices whose due date is at or before the as-of date
    if (inv.dueDate > asOfDate) continue;
    const ageDays = Math.max(0, Math.floor((asOfDate - inv.dueDate) / DAY));
    const bucket = bucketForAgeDays(ageDays);
    const dealerName = dealerNameMap.get(inv.dealerId) ?? inv.dealerId;
    const row = ensureRow(inv.dealerId, dealerName);
    row.totalInBaseCents += balance;
    row.buckets[bucket] += balance;
    row.items.push({
      kind: "invoice",
      label: inv.invoiceNumber,
      balanceInBaseCents: balance,
      ageDays,
      bucket,
    });
  }

  // Fold in dealer opening AR balances as phantom invoices
  for (const d of dealers) {
    const openingBalance = d.openingBalanceBaseCents ?? 0n;
    const openingAsOf = d.openingBalanceAsOf ?? null;
    if (openingBalance <= 0n) continue;
    if (openingAsOf === null || openingAsOf > asOfDate) continue;
    const ageDays = Math.max(0, Math.floor((asOfDate - openingAsOf) / DAY));
    const bucket = bucketForAgeDays(ageDays);
    const row = ensureRow(d._id, d.displayName);
    row.totalInBaseCents += openingBalance;
    row.buckets[bucket] += openingBalance;
    row.hasOpeningBalance = true;
    row.items.push({
      kind: "opening",
      label: "Opening balance",
      balanceInBaseCents: openingBalance,
      ageDays,
      bucket,
    });
  }

  const rows = Array.from(map.values()).sort((a, b) =>
    Number(b.totalInBaseCents - a.totalInBaseCents),
  );
  const bucketTotals: Record<ArAgingBucket, bigint> = {
    "0-30": 0n,
    "31-60": 0n,
    "61-90": 0n,
    "90+": 0n,
  };
  let grand = 0n;
  for (const r of rows) {
    grand += r.totalInBaseCents;
    for (const k of Object.keys(bucketTotals) as ArAgingBucket[]) {
      bucketTotals[k] += r.buckets[k];
    }
  }
  return { asOfDate, rows, bucketTotals, grandTotalInBaseCents: grand };
}

/* ── Component ────────────────────────────────────────────────── */

function ArAgingPage() {
  const [asOf, setAsOf] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const asOfMs = new Date(asOf).getTime();

  const [invoices] = usePersistentMockState<MockInvoice[]>("invoices", mockInvoices);
  const [dealers] = usePersistentMockState<MockDealer[]>("dealers", mockDealers);

  const report = useMemo(
    () => deriveArAging(asOfMs, invoices, dealers),
    [asOfMs, invoices, dealers],
  );
  const printRef = useRef<HTMLDivElement>(null);

  function handleCsv(extension: "csv" | "xls") {
    type ArRow = {
      dealer: string;
      totalCents: bigint;
      bucket0: bigint;
      bucket31: bigint;
      bucket61: bigint;
      bucket90: bigint;
      currency: string;
    };
    const rows: ArRow[] = report.rows.map((r) => ({
      dealer: r.dealerName,
      totalCents: r.totalInBaseCents,
      bucket0: r.buckets["0-30"],
      bucket31: r.buckets["31-60"],
      bucket61: r.buckets["61-90"],
      bucket90: r.buckets["90+"],
      currency: "BDT",
    }));
    rows.push({
      dealer: "GRAND TOTAL",
      totalCents: report.grandTotalInBaseCents,
      bucket0: report.bucketTotals["0-30"],
      bucket31: report.bucketTotals["31-60"],
      bucket61: report.bucketTotals["61-90"],
      bucket90: report.bucketTotals["90+"],
      currency: "BDT",
    });
    const columns: ExportColumn<ArRow>[] = [
      { key: "dealer", label: "Dealer" },
      { key: "totalCents", label: "Total" },
      { key: "bucket0", label: "0-30" },
      { key: "bucket31", label: "31-60" },
      { key: "bucket61", label: "61-90" },
      { key: "bucket90", label: "90+" },
      { key: "currency", label: "Currency" },
    ];
    downloadCsv(exportFilename("ar-aging", extension), toCsv(rows, columns));
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "AR Aging",
      subtitle: `As of ${formatDate(asOfMs, { style: "medium" })}`,
    });
  }

  const columns = useMemo<ColumnDef<ArAgingRow>[]>(
    () => [
      {
        accessorKey: "dealerName",
        header: "Dealer",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2 text-(--text-primary)">
            {row.original.dealerName}
            {row.original.hasOpeningBalance ? (
              <Badge
                variant="info"
                size="sm"
                title="This dealer's outstanding balance includes an opening-balance carry-forward."
              >
                Opening
              </Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: "total",
        header: "Total outstanding",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums font-semibold">
            {formatMoney(row.original.totalInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      bucketColumn("0-30", "0-30 days", "info"),
      bucketColumn("31-60", "31-60", "neutral"),
      bucketColumn("61-90", "61-90", "warning"),
      bucketColumn("90+", "90+", "danger"),
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="AR Aging"
        description={`Open dealer receivables aged as of ${formatDate(asOfMs, { style: "medium" })}.`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="ar-asof">As of</Label>
          <Input
            id="ar-asof"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="w-44"
          />
        </div>
      </div>

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        aria-label="AR aging KPIs"
      >
        <KpiCard
          caption="Grand total"
          value={formatMoneySmart(report.grandTotalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.rows.length} dealers`}
        />
        <KpiCard
          caption="0-30 days"
          value={formatMoneySmart(report.bucketTotals["0-30"], "BDT", {
            locale: "en-BD",
          })}
          hint="Current"
        />
        <KpiCard
          caption="31-60 days"
          value={formatMoneySmart(report.bucketTotals["31-60"], "BDT", {
            locale: "en-BD",
          })}
          hint="Watch"
        />
        <KpiCard
          caption="61-90 days"
          value={formatMoneySmart(report.bucketTotals["61-90"], "BDT", {
            locale: "en-BD",
          })}
          hint="Overdue"
        />
        <KpiCard
          caption="90+ days"
          value={formatMoneySmart(report.bucketTotals["90+"], "BDT", {
            locale: "en-BD",
          })}
          hint="Past due"
        />
      </section>

      <DataTable
        data={report.rows}
        columns={columns}
        getRowId={(r) => r.dealerId}
        emptyTitle="No outstanding AR"
        emptyDescription="Every dealer is settled as of this date."
        emptyIcon={<Clock className="h-5 w-5" aria-hidden />}
      />

      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[13px] grid grid-cols-6 gap-2 font-mono tabular-nums">
        <div className="col-span-2 font-semibold text-(--text-primary)">
          Bucket totals
        </div>
        <BucketCell label="0-30" amount={report.bucketTotals["0-30"]} />
        <BucketCell label="31-60" amount={report.bucketTotals["31-60"]} />
        <BucketCell label="61-90" amount={report.bucketTotals["61-90"]} />
        <BucketCell label="90+" amount={report.bucketTotals["90+"]} />
      </div>
    </div>
  );
}

function bucketColumn(
  key: ArAgingBucket,
  header: string,
  tone: "info" | "neutral" | "warning" | "danger",
): ColumnDef<ArAgingRow> {
  return {
    id: `bucket_${key}`,
    header,
    meta: { numeric: true, mono: true },
    cell: ({ row }) => {
      const v = row.original.buckets[key];
      return (
        <span
          className={cn(
            "font-mono tabular-nums",
            v === 0n
              ? "text-(--text-tertiary)"
              : tone === "danger"
                ? "text-(--danger)"
                : tone === "warning"
                  ? "text-(--text-primary)"
                  : "text-(--text-secondary)",
          )}
        >
          {v === 0n ? "—" : formatMoney(v, "BDT", { locale: "en-BD" })}
        </span>
      );
    },
  };
}

function BucketCell({
  label,
  amount,
}: {
  label: string;
  amount: bigint;
}) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase text-(--text-tertiary)">{label}</div>
      <div className="text-(--text-primary)">
        {formatMoney(amount, "BDT", { locale: "en-BD" })}
      </div>
    </div>
  );
}
